import React, { useState, useEffect, useContext, ReactElement } from "react";
import { RouteComponentProps, Redirect } from "react-router-dom";
import {
  Button,
  Paper,
  Grid,
  makeStyles,
  Typography,
  TableCell,
  Divider,
  Link,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import { FilterList as FilterListIcon } from "@material-ui/icons";
import UserContext from "../../UserContext";
import useInput from "../../hooks/input/useInput";
import useTable from "../../hooks/table/useTable";
import LinkAdapter from "../../components/shared/common/LinkAdapter";
import TrTable from "../../components/shared/controls/TrTable";
import TrTableHeadMetadata from "../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../models/shared/TrTableBodyData";
import PagedResult from "../../models/shared/PagedResult";
import FinancingRequestList from "../../models/financing-request/FinancingRequestList";
import FinancingRequestApiClient from "../../api/FinancingRequestApiClient";
import {
  financingRequestNewUrl,
  financingRequestEditUrl,
  financingRequestDetailsUrl,
  financingRequestValidityRangeEditUrl,
} from "../../urls";
import FinancingRequestStatusEnum from "../../models/financing-request-status/FinancingRequestStatusEnum";
import { numberFormat, nameof } from "../../utilities/utilities";
import YearFilter from "../../models/shared/YearFilter";
import MonthFilter from "../../models/shared/MonthFilter";
import FinancingRequestFilter from "../../models/financing-request/FinancingRequestFilter";
import FinancingRequestFilterValue from "../../models/financing-request/FinancingRequestFilterValue";
import TrSelect from "../shared/controls/TrSelect";
import TrSelectData from "../../models/shared/TrSelectData";
import TrKeyboardDatePicker from "../shared/controls/TrKeyboardDatePicker";
import ButtonConfirmDialog from "../shared/common/ButtonConfirmDialog";
import FinancingRequestStatusElement from "../shared/controls/FinancingRequestStatusElement";

export default function FinancingRequestListPage({
  history,
}: RouteComponentProps) {
  const classes = useStyles();
  const defaultFilterValues = {
    schoolYearId: "",
    DateFrom: null,
    DateTo: null,
  };

  const { user } = useContext(UserContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [filters, setFilters] = useState<FinancingRequestFilter>(
    {} as FinancingRequestFilter
  );
  const [filterValue, setFilterValue] = useState<FinancingRequestFilterValue>(() => {
    const savedFilterValues = sessionStorage.getItem("financingRequestFilterValues");
    return savedFilterValues ? JSON.parse(savedFilterValues) : {
      schoolYearId: "",
      DateFrom: null,
      DateTo: null,
    };
  });

  const [financingRequestsPaged, setFinancingRequestsPaged] = useState<
    PagedResult<FinancingRequestList>
  >({} as PagedResult<FinancingRequestList>);

  const [canAddNew, setCanAddNew] = useState<boolean>(false);

  const { page, pageSize, handleChangePage, handleChangepageSize } = useTable();
  const { handleSelectChange, handleDateChange } = useInput();

  useEffect(() => {
    getFilters();
  }, [tabIndex]);

  useEffect(() => {
    canAddRequest();
  }, []);

  useEffect(() => {
    // if (filterValue.schoolYearId) {
      search();
    // }
  }, [filterValue, page, pageSize]);

  useEffect(() => {
    sessionStorage.setItem("financingRequestFilterValues", JSON.stringify(filterValue));
  }, [filterValue]);

  const clearFilters = () => {
    setFilterValue(defaultFilterValues);
    sessionStorage.setItem("financingRequestFilterValues", JSON.stringify(defaultFilterValues));
  };


  async function canAddRequest() {
    const apiClient = new FinancingRequestApiClient();
    const canAdd = await apiClient.getCanAddNewRequest();
    setCanAddNew(canAdd);
  }

  async function getFilters() {
    const apiClient = new FinancingRequestApiClient();
    const searchFilters = await apiClient.getSearchFilters();

    const savedFilterValues = sessionStorage.getItem("financingRequestFilterValues");
    if (savedFilterValues) {
      setFilterValue(JSON.parse(savedFilterValues));
    } else {
      setFilterValue({
        schoolYearId: searchFilters.schoolYears.length > 0 ? searchFilters.schoolYears[0].id : "",
        DateFrom: null,
        DateTo: null,
      });
    }

    setFilters(searchFilters);
  }


  async function search() {
    const apiClient = new FinancingRequestApiClient();
    const requests = await apiClient.search(filterValue, page + 1, pageSize);

    setFinancingRequestsPaged(requests);
  }

  const tableHeadMetadata: TrTableHeadMetadata<FinancingRequestList>[] = [
    { id: "schoolYear", label: "Šk. godina", dataName: "schoolYear" },
    {
      id: "classificationMark",
      label: "Klasa",
      dataName: "classificationMark",
    },
    {
      id: "registerNumber",
      label: "Urudžbeni broj",
      dataName: "registerNumber",
    },
    { id: "studentsNumber", label: "Broj učenika", dataName: "studentsNumber" },
    {
      id: "validFrom",
      label: "Datum od kada vrijedi zahtjev",
      dataName: "validFrom",
    },
    {
      id: "validTo",
      label: "Datum do kada vrijedi zahtjev",
      dataName: "validTo",
    },
    {
      id: "status",
      label: "Status",
      dataName: "statusName",
      dataFunc: (f: FinancingRequestList) => (
        <FinancingRequestStatusElement
          statusId={f.statusId}
          statusName={f.statusName}
        />
      ),
    },
    {
      id: "viewBtn",
      label: "",
      dataFunc: (f: FinancingRequestList) => (
        <div className={classes.rowRight}>
          {f.statusId == FinancingRequestStatusEnum.Draft ||
            f.statusId == FinancingRequestStatusEnum.Returned ? (
            <>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={() =>
                  history.push(financingRequestEditUrl(f.id.toString()))
                }
              >
                UREDI
              </Button>
            </>
          ) : f.statusId == FinancingRequestStatusEnum.Approved ? (
            <>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={() =>
                  history.push(
                    financingRequestValidityRangeEditUrl(f.id.toString())
                  )
                }
              >
                IZMIJENI
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className={classes.marginLeft}
                onClick={() =>
                  history.push(financingRequestDetailsUrl(f.id.toString()))
                }
              >
                PREGLED
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() =>
                history.push(financingRequestDetailsUrl(f.id.toString()))
              }
            >
              PREGLED
            </Button>
          )}
        </div>
      ),
    },
  ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: financingRequestsPaged.data,
  };

  const handleBodyCellCreated = (tableCell: ReactElement, rowData: any) => {
    return (
      <TableCell
        key={tableCell.key}
        {...tableCell.props}
        className={
          rowData.statusId === FinancingRequestStatusEnum.Rejected
            ? classes.redColor
            : ""
        }
      />
    );
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Typography variant="h5">
            Lista godišnjih zahtjeva za sufinanciranjem
          </Typography>
        </Grid>
        <Grid item sm={12} className={classes.itemRightAlign}>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            component={LinkAdapter}
            to={financingRequestNewUrl()}
            disabled={!canAddNew}
          >
            <AddIcon />
            Dodaj novi zahtjev
          </Button>
        </Grid>
      </Grid>
      <div className={classes.filterBox}>
        <Typography className={classes.filterTitle}>
          <FilterListIcon /> Filteri
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TrSelect
              label={"Šk. godina"}
              value={filterValue.schoolYearId}
              name={nameof<typeof filterValue>("schoolYearId")}
              fullWidth={true}
              selectData={
                {
                  valueProp: "id",
                  textProp: "name",
                  data: filters.schoolYears || [],
                } as TrSelectData<YearFilter>
              }
              onChange={(e) => handleSelectChange(e, setFilterValue)}
            />
          </Grid>
          <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
            <TrKeyboardDatePicker
              format="dd. MM. yyyy."
              label="Datum od"
              helperText="Datum od kada vrijedi zahtjev"
              value={filterValue.DateFrom}
              className={classes.textField}
              onChange={(e) => handleDateChange("DateFrom", e, setFilterValue)}
            />
          </Grid>
          <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
            <TrKeyboardDatePicker
              format="dd. MM. yyyy."
              label="Datum do"
              helperText="Datum od kada vrijedi zahtjev"
              value={filterValue.DateTo}
              className={classes.textField}
              onChange={(e) => handleDateChange("DateTo", e, setFilterValue)}
            />
          </Grid>
        </Grid>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ textAlign: 'right' }}>
          <Link component="button" onClick={clearFilters} variant="body2" style={{ cursor: 'pointer' }}>
            Očisti filtere
          </Link>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item lg={12}>
          <Paper>
            <TrTable
              hover={true}
              headMetadata={tableHeadMetadata}
              bodyData={tableBodyData}
              enablePagination={true}
              page={page}
              pageSize={pageSize}
              totalCount={financingRequestsPaged.count}
              noDataMessage={"Trenutno nema zahtjeva za sufinanciranjem"}
              onChangePage={handleChangePage}
              onChangePageSize={handleChangepageSize}
              onBodyCellCreated={handleBodyCellCreated}
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  filterBox: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filterTitle: {
    ...theme.typography.button,
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
  },
  button: {
    textTransform: "none",
  },
  itemRightAlign: {
    textAlign: "right",
  },
  redColor: {
    color: "#d9534f",
  },
  rowRight: {
    width: "100%",
    textAlign: "right",
  },
  textField: {
    width: "100%",
    maxWidth: "100%",
  },
  marginLeft: {
    marginLeft: theme.spacing(2),
  },
}));
